import { useEffect, useState, useCallback } from "react";
import { api, errMsg } from "@/lib/api";
import { PageHeader, Loading, ErrorState, EmptyState } from "@/components/common";
import { Card, CardContent } from "@/components/ui/card";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { BookOpen, ChevronRight } from "lucide-react";

const asList = (d) => (Array.isArray(d) ? d : d?.items || d?.articles || d?.results || []);

export default function Education() {
  const [articles, setArticles] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [active, setActive] = useState(null);

  const load = useCallback(async () => {
    setLoading(true); setError("");
    try { setArticles(asList(await api.education())); }
    catch (e) { setError(errMsg(e)); }
    finally { setLoading(false); }
  }, []);

  useEffect(() => { load(); }, [load]);

  return (
    <div>
      <PageHeader title="Sezione educazionale" subtitle="Approfondimenti su scoliosi e riabilitazione." />

      {loading ? <Loading /> : error ? <ErrorState message={error} onRetry={load} /> :
        articles.length === 0 ? <EmptyState title="Nessun articolo" /> : (
          <div className="space-y-3" data-testid="education-list">
            {articles.map((a, i) => (
              <Card
                key={a.id || a._id || i}
                className="cursor-pointer border-border/70 transition-all hover:border-primary/40 hover:shadow-sm"
                onClick={() => setActive(a)}
                data-testid={`article-card-${i}`}
              >
                <CardContent className="flex items-center gap-4 p-4 sm:p-5">
                  <div className="grid h-12 w-12 shrink-0 place-items-center rounded-2xl bg-[#e6f0e9] text-[#3f7a70]">
                    <BookOpen className="h-6 w-6" />
                  </div>
                  <div className="min-w-0 flex-1">
                    <p className="font-bold leading-snug text-[#1f3a5f]">{a.title || a.name}</p>
                    <p className="mt-0.5 line-clamp-2 text-sm text-muted-foreground">{a.summary || a.excerpt || a.content || a.body}</p>
                    {a.category && (
                      <span className="mt-2 inline-block rounded-full bg-[#eaf1f6] px-3 py-0.5 text-xs font-medium text-primary">{a.category}</span>
                    )}
                  </div>
                  <ChevronRight className="h-5 w-5 shrink-0 text-muted-foreground" />
                </CardContent>
              </Card>
            ))}
          </div>
        )}

      <Dialog open={!!active} onOpenChange={(o) => !o && setActive(null)}>
        <DialogContent className="max-h-[85vh] overflow-y-auto sm:max-w-2xl">
          <DialogHeader><DialogTitle className="text-2xl">{active?.title || active?.name}</DialogTitle></DialogHeader>
          <DialogDescription className="sr-only">Contenuto dell'articolo educativo</DialogDescription>
          {active?.category && <span className="w-fit rounded-full bg-[#eaf1f6] px-3 py-0.5 text-xs font-medium text-primary">{active.category}</span>}
          <p className="mt-2 whitespace-pre-line text-sm leading-relaxed text-foreground/85">{active?.content || active?.body || active?.summary}</p>
        </DialogContent>
      </Dialog>
    </div>
  );
}
